import { Button } from "@/src/components/Button";
import { ApiCallsTypes } from "@/src/components/pages/StepPage/components/apiCalls/contants";
import { apiCallsModalStore } from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import {
    AgreementModalData,
    ApiCallModalType,
} from "@/src/components/pages/StepPage/components/apiCalls/types";
import { useHandleApiCall } from "@/src/components/pages/StepPage/components/apiCalls/useHandleApiCall";
import { ParsedValue } from "@/src/components/TextWithValue";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { strings } from "@/src/shared/strings";
import { IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useStepsNavigation } from "../useStepsNavigation";
import { useOtpTemplateCode } from "@/src/components/pages/StepPage/components/apiCalls/Modals/useOtpTemplateCode";
import { useFormContext } from "react-hook-form";
import { ModalHeader } from "./components/ModalHeader";
import { isMobile } from "react-device-detect";
import { useAnalytics } from "../../useAnalytics/useAnalytics";
import { logError } from "@/src/api/server-actions/analytics";

export function convertToParagraphsAndLists(
    nodes: (string | JSX.Element | null)[]
): JSX.Element[] {
    const elements: JSX.Element[] = [];
    const paragraphBuffer: (string | JSX.Element)[] = [];
    let brCounter = 0;

    const flushParagraph = () => {
        if (paragraphBuffer.length === 0) return;
        elements.push(
            <p key={elements.length}>
                {paragraphBuffer.reduce<(string | JSX.Element)[]>((acc, curr, idx) => {
                    if (typeof curr !== "string") {
                        const prev = acc[idx - 1];
                        if (typeof prev === "string" && !prev.endsWith(" ")) {
                            acc[idx - 1] = prev + " ";
                        }
                    }
                    acc.push(curr);
                    return acc;
                }, [])}
            </p>
        );
        paragraphBuffer.length = 0;
    };

    nodes.forEach(node => {
        if (typeof node === "string") {
            const parts = node.split("\n");
            parts.forEach((part, i) => {
                paragraphBuffer.push(part);
                if (i < parts.length - 1) {
                    paragraphBuffer.push(<br key={`br-${brCounter++}`} />);
                }
            });
        } else if (node) {
            paragraphBuffer.push(node);
        }
    });

    flushParagraph();
    return elements;
}

export const AgreementModal: React.FC<AgreementModalData> = ({
                                                                 cancelText = "скасувати",
                                                                 confirmText = "Підтвердити договір",
                                                                 description = "",
                                                                 linkText = "Посилання на пропозицію",
                                                                 onConfirmClick,
                                                                 onCancelClick,
                                                                 onLinkClick,
                                                                 warnText,
                                                                 defaultValues,
                                                                 title = strings.orderAgreementModal.confirmAgreement
                                                             }) => {
    const { trackAnalyticEvent } = useAnalytics();

    const { close, open } = apiCallsModalStore();
    const { handleApiCallByName } = useHandleApiCall(true);
    const { goBack, goNext, goToStep } = useStepsNavigation();
    const { getValues } = useFormContext();
    const templateCode = useOtpTemplateCode();
    const [isSubmitPending, setIsSubmitPending] = useState(false);
    const [isLinkPending, setIsLinkPending] = useState(false);

    const onCancel = () => {
        trackAnalyticEvent("confirm_contract");

        if (onCancelClick) {
            onCancelClick({
                nextStep: goNext,
                prevStep: goBack,
                specificStep: goToStep,
                handleApiCallByName
            });
        } else {
            close();
        }
    };
    const onSubmit = async () => {
        trackAnalyticEvent("confirm_cancel");
        setIsSubmitPending(true);

        if (onConfirmClick) {
            await onConfirmClick({
                nextStep: goNext,
                prevStep: goBack,
                specificStep: goToStep,
                handleApiCallByName
            });
        } else {
            try {
                const response = await handleApiCallByName(
                    ApiCallsTypes.SEND_IDENTIFICATION_CODE,
                    true,
                    {
                        formDataOverride: {
                            ...getValues(),
                            templateCode: templateCode,
                        },
                    },
                );
                if (response) {
                    open(ApiCallModalType.OTP, response);
                } else {
                    logError("[AgreementModal] SendIdentificationCode returned empty response — OTP modal not opened");
                }
            } catch (error) {
                logError("[AgreementModal] SendIdentificationCode failed", error);
            }
        }
        setIsSubmitPending(false);
    };

    const onLink = async () => {
        trackAnalyticEvent("offer_link");
        const payload = getValues("offerPrintout");
        if (!payload) return;
        setIsLinkPending(true);

        const queryParams = new URLSearchParams({
            uuid: payload.uuid,
            fileName: payload.fileName,
            sourceId: payload.sourceId,
        }).toString();

        window.open(`/download?${queryParams}`, "_blank", "noopener,noreferrer");
        setIsLinkPending(false);
    };

    return (
        <Stack
            className={
                "max-w-[524px] rounded-xl bg-white center-modal max-md:h-full overflow-y-auto max-md:animate-up-modal max-md:items-start max-md:rounded-none max-md:bottom-modal"
            }
        >
            {isMobile && (
                <div className="h-14 w-full md:hidden">
                    <ModalHeader onCancel={onCancel} />
                </div>
            )}
            <div className="flex flex-col items-center gap-4 p-6 flex-1 overflow-y-auto max-md:h-full">
                <Stack className={"w-full flex-center-row"}>
                    <div className="flex flex-1 md:justify-center">
                        <Typography
                            variant={"h1"}
                            component={"h3"}
                            className={
                                "flex-0 w-fit justify-center text-center text-2xl font-semibold max-md:text-start"
                            }
                        >
                            {title}
                        </Typography>
                    </div>
                    <IconButton onClick={onCancel} className="max-md:hidden">
                        <WebShopIcon name={"close"} className="fill-blue" />
                    </IconButton>
                </Stack>
                <Stack className="flex flex-col">
                    {warnText && (
                        <Typography component="p" align="center">
                            <ParsedValue
                                value={warnText}
                                defaultValues={defaultValues}
                                makeBold={true}
                                style={{ color: "#FF0000", fontSize: "20px", textAlign: "center" }}
                            />
                        </Typography>
                    )}
                    <Typography component={"p"}>
                        {typeof description === 'string' ? <ParsedValue value={description} /> : description}
                    </Typography>
                    <Button
                        variant="text"
                        fullWidth={false}
                        startIcon={<WebShopIcon name="link" />}
                        loading={isLinkPending}
                        className="w-fit justify-start normal-case"
                        onClick={onLinkClick ?? onLink}
                    >
                        {linkText}
                    </Button>
                </Stack>
                <Stack className="flex w-full flex-col items-center justify-end gap-3 max-md:mt-auto md:flex-row">
                    <Button
                        onClick={onCancel}
                        variant="outlined"
                        className="w-fit max-md:w-full"
                    >
                        {cancelText}
                    </Button>
                    <Button
                        onClick={onSubmit}
                        loading={isSubmitPending}
                        className="w-fit max-md:w-full"
                    >
                        {confirmText}
                    </Button>
                </Stack>
            </div>
        </Stack>
    );
};
